/**
 * OutsideOrdersQueue (/appraisal-queue/outside) — the Appraisal Queue's second
 * tab: every outside party valuation order (vendor / AMC, RicherValues, AVM)
 * across all loans. Internal reviews stay on the first tab.
 *
 * Contract: eastview-os/frontend_documentation/appraisal-review.md § Secondary
 * orders — `GET /appraisal-review/secondary-orders/queue` (never `internal`,
 * oldest first, capped at 500). The rows carry only `dealId`, so the loan's
 * stage / closing date / inactive flag come from the parent's `loanIndex`
 * (both pipeline lists, see queueToolbar), and the dead / inactive / closed
 * filter is applied here — the endpoint doesn't apply one.
 *
 * Row click opens the split view: list rail · order detail with the actions
 * its status allows (Mark placed / Mark received / Cancel — the same dialogs
 * as the loan's Orders tab) · the loan itself, embedded on its Orders tab.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Tooltip from "@mui/material/Tooltip";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import StorefrontOutlined from "@mui/icons-material/StorefrontOutlined";

import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import {
  INACTIVE_TOOLTIP,
  PillGroup,
  QueueScopeRow,
  QueueStatusBar,
  ToolbarSwitch,
  dateOnlyComparator,
  dealKey,
  loanStageColumnDefs,
  readHideInactive,
  withLoanStage,
  writeHideInactive,
} from "../../components/queueToolbar/queueToolbar";
import {
  SECONDARY_STATUS_META,
  SecondaryStatusChip,
  secondaryStatusMeta,
} from "../../components/appraisalOrders/secondaryOrderUi";
import {
  canCancel,
  canMarkReceived,
  canMarkSubmitted,
  secondaryOrderErrorMessage,
  secondaryOrderId,
  secondaryValuationOrdersApi,
} from "../../api/secondaryValuationOrdersApi";
import SecondaryOrderActionDialogs, {
  SECONDARY_ACTION,
} from "../LoanDrilldownScreen/tabs/LoanDrilldownOrdersTab/components/SecondaryOrderActionDialogs";
import LoanDrilldownScreen from "../LoanDrilldownScreen/LoanDrilldownScreen";
import { filePreviewSetData } from "../../redux/reducers/filePreviewReducer";
import { getUserFullNameFromEmail } from "../../utils/functions/getUserFullNameFromEmail";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import formatDollar from "../../_src_shared/utils/functions/formatDollar";
import { status as statusTones } from "../../theme/colors";
import {
  AppraisalQueueTabs,
  correspondentColumnDef,
  OUTSIDE_BASE,
  RefreshButton,
  SearchBox,
  QUEUE_MAX_WIDTH,
  StatCard,
  T,
} from "./appraisalQueueAtoms";

const ALL = "__all__";

// Kind labels (the backend's `SecondaryKind`, verbatim keys).
const KIND_LABEL = {
  vendor_amc: "Vendor / AMC",
  richer_values: "RicherValues",
  avm: "AVM",
};
const vendorLabel = (o) =>
  [KIND_LABEL[o?.kind] || o?.kind || null, o?.provider || null]
    .filter(Boolean)
    .join(" · ") || "—";

const LANES = [
  { key: ALL, label: "All" },
  ...["requested", "submitted", "received", "tied_out", "cancelled"].map(
    (k) => ({
      key: k,
      label: SECONDARY_STATUS_META[k].label,
      dot: SECONDARY_STATUS_META[k].tone.solid,
    }),
  ),
];

const SCOPE = { ALL: "all", PRECLOSE: "preclose", ACQ: "acquisition" };
const SCOPE_PILLS = [
  { key: SCOPE.ALL, label: "All" },
  { key: SCOPE.PRECLOSE, label: "Pre-Close", dot: statusTones.info.solid },
  { key: SCOPE.ACQ, label: "Acquisition", dot: statusTones.success.solid },
];
const inScope = (o, scope) =>
  scope === SCOPE.ALL ||
  (scope === SCOPE.ACQ ? o.loanIsAcquisition : !o.loanIsAcquisition);

const OPEN = new Set(["requested", "submitted"]);
const HIDE_INACTIVE_KEY = "appraisalQueue.outside.hideInactiveLoans";

const oidStr = (v) => {
  if (v == null) return null;
  if (typeof v === "object") return v.$oid || v._id || null;
  return String(v);
};

// Today's date in Eastern (the desk's zone) as YYYY-MM-DD.
const todayYmd = () => formatDateTime(new Date().toISOString(), "yyyy-MM-dd");

const isOverdue = (o, today) =>
  OPEN.has(o?.status) &&
  !!o?.requestedByDate &&
  String(o.requestedByDate).slice(0, 10) < today;

const matchesSearch = (o, q) => {
  if (!q) return true;
  const hay = [
    o?.subjectPropertyAddress,
    o?.loanNumber,
    o?.requestedBy,
    o?.provider,
    KIND_LABEL[o?.kind],
    o?.providerOrderId,
    o?.loanStage,
    o?._loan?.originator,
  ]
    .filter((v) => v != null)
    .join(" ")
    .toLowerCase();
  return hay.includes(q);
};

const orderHref = (o) =>
  `${OUTSIDE_BASE}/${dealKey(o?.dealId)}/orders?order=${secondaryOrderId(o)}`;

/* ── small UI atoms ─────────────────────────────────────────────────────── */

const EmptyState = ({ hasAny }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 1,
      py: 8,
      px: 4,
      textAlign: "center",
    }}
  >
    <StorefrontOutlined sx={{ fontSize: 34, color: T.ink300 }} />
    <Box sx={{ fontSize: 15, fontWeight: 700, color: T.ink900 }}>
      {hasAny ? "No orders match this view" : "No external orders yet"}
    </Box>
    <Box sx={{ fontSize: 13, color: T.ink500, maxWidth: 400 }}>
      {hasAny
        ? "Try a different status, clear the search, turn off “Ordered by me”, or include Dead / Inactive / Closed Loans."
        : "Appraisal reviews ordered from an outside vendor or AMC on a loan's Orders tab appear here."}
    </Box>
  </Box>
);

const ReceivedCell = ({ order }) => {
  const r = order?.received;
  if (!r || (r.asIsValue == null && r.arv == null)) {
    return (
      <Box component="span" sx={{ color: T.ink300 }}>
        —
      </Box>
    );
  }
  return (
    <Box sx={{ lineHeight: 1.25 }}>
      {r.asIsValue != null && (
        <Box sx={{ fontSize: 13, color: T.ink900 }}>
          {formatDollar(r.asIsValue, "—")}{" "}
          <Box component="span" sx={{ fontSize: 11.5, color: T.ink500 }}>
            as-is
          </Box>
        </Box>
      )}
      {r.arv != null && (
        <Box sx={{ fontSize: 11.5, color: T.ink500 }}>
          {formatDollar(r.arv, "—")} after repair
        </Box>
      )}
    </Box>
  );
};

/* ── full table view (index route) ──────────────────────────────────────── */

const FullView = ({
  rows,
  filtered,
  stats,
  laneCounts,
  lane,
  setLane,
  scope,
  setScope,
  scopeCounts,
  search,
  setSearch,
  mineOnly,
  setMineOnly,
  hideInactive,
  setHideInactive,
  hiddenCount,
  loading,
  error,
  columnDefs,
  onRefresh,
}) => {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        background: T.bg,
        flex: "1 0 auto",
        minHeight: "100%",
        fontFamily: "inherit",
      }}
    >
      <Container
        maxWidth={false}
        sx={{ maxWidth: QUEUE_MAX_WIDTH, pt: "36px", pb: "72px" }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Box>
            <Box
              sx={{
                fontSize: 23,
                fontWeight: 700,
                letterSpacing: "-.01em",
                color: T.ink900,
              }}
            >
              Appraisal Queue
            </Box>
            <Box sx={{ color: T.ink500, mt: "5px", fontSize: 13.5 }}>
              Claim, review, and complete internal appraisal reviews, and track
              outside party orders, across all loans.
            </Box>
          </Box>
          <Box sx={{ ml: "auto" }}>
            <RefreshButton onClick={onRefresh} loading={loading} />
          </Box>
        </Box>
        <AppraisalQueueTabs />

        <QueueScopeRow label="Pipeline">
          <PillGroup
            options={SCOPE_PILLS}
            value={scope}
            onChange={setScope}
            counts={scopeCounts}
          />
          <ToolbarSwitch
            checked={mineOnly}
            onChange={setMineOnly}
            label="Ordered by me"
          />
          <ToolbarSwitch
            checked={!hideInactive}
            onChange={(on) => setHideInactive(!on)}
            label="Include Dead / Inactive / Closed Loans"
            note={
              hideInactive && hiddenCount > 0 ? `(${hiddenCount} Hidden)` : null
            }
            tooltip={INACTIVE_TOOLTIP}
          />
        </QueueScopeRow>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2,1fr)", md: "repeat(4,1fr)" },
            gap: 2,
            m: "18px 0",
          }}
        >
          <StatCard
            n={stats.requested}
            label="Waiting to be placed"
            color={SECONDARY_STATUS_META.requested.tone.solid}
          />
          <StatCard
            n={stats.submitted}
            label="Placed with vendor"
            color={SECONDARY_STATUS_META.submitted.tone.solid}
          />
          <StatCard
            n={stats.received}
            label="Received"
            color={SECONDARY_STATUS_META.received.tone.solid}
          />
          <StatCard
            n={stats.overdue}
            label="Past their needed by date"
            color={statusTones.error.solid}
          />
        </Box>

        <QueueStatusBar
          pills={
            <PillGroup
              options={LANES}
              value={lane}
              onChange={setLane}
              counts={laneCounts}
            />
          }
          shown={filtered.length}
          total={scopeCounts[scope] ?? 0}
          search={search}
          onSearch={setSearch}
          placeholder="Search loan #, property, vendor…"
        />

        <Box
          sx={{
            background: T.card,
            border: `1px solid ${T.line}`,
            borderRadius: T.radius,
            boxShadow: T.shadow,
            overflow: "hidden",
          }}
        >
          {error ? (
            <Box sx={{ p: 4, color: T.red, fontSize: 13.5 }}>{error}</Box>
          ) : loading ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
              <CircularProgress size={26} />
            </Box>
          ) : filtered.length === 0 ? (
            <EmptyState hasAny={rows.length > 0} />
          ) : (
            <AgGridTableWrapper height="auto" noBorder noBorderRadius>
              <AgGridReact
                rowData={filtered}
                columnDefs={columnDefs}
                defaultColDef={{
                  resizable: true,
                  sortable: true,
                  suppressHeaderMenuButton: true,
                  floatingFilter: true,
                  minWidth: 90,
                  cellStyle: { display: "flex", alignItems: "center" },
                }}
                getRowId={(p) => secondaryOrderId(p.data)}
                domLayout="autoHeight"
                pagination
                paginationPageSize={25}
                rowHeight={56}
                headerHeight={40}
                floatingFiltersHeight={38}
                suppressCellFocus
                rowStyle={{ cursor: "pointer" }}
                onCellClicked={(e) => {
                  // Open the split view except when clicking the loan deep-link.
                  if (e.colDef?.headerName === "Loan") return;
                  if (e.data?.dealId) navigate(orderHref(e.data));
                }}
              />
            </AgGridTableWrapper>
          )}
        </Box>
      </Container>
    </Box>
  );
};

/* ── split view: order detail (column 2) ────────────────────────────────── */

const Section = ({ label, children }) => (
  <Box sx={{ px: "18px", py: "14px", borderTop: `1px solid ${T.line}` }}>
    <Box
      sx={{ fontSize: 11, fontWeight: 600, color: T.ink500, mb: "8px" }}
    >
      {label}
    </Box>
    {children}
  </Box>
);

const Fact = ({ k, v }) => (
  <Box sx={{ display: "flex", gap: 1.5, fontSize: 12.5, lineHeight: 1.7 }}>
    <Box sx={{ color: T.ink500, width: 120, flexShrink: 0 }}>{k}</Box>
    <Box sx={{ color: T.ink900, fontWeight: 500, minWidth: 0 }}>{v}</Box>
  </Box>
);

// A loan document chip that opens the file preview. Disabled when the doc
// can't be resolved (the loan is still loading, or it was deleted since).
const DocChip = ({ doc, onPreview }) => (
  <Chip
    icon={<DescriptionOutlined />}
    label={doc?.file_display_name || doc?.docType || "Document not found"}
    disabled={!doc}
    onClick={doc ? () => onPreview(doc) : undefined}
    sx={{
      height: 30,
      fontSize: 12.5,
      fontWeight: 500,
      maxWidth: "100%",
      background: "#fff",
      border: `1px solid ${T.line}`,
      color: T.ink700,
      "& .MuiChip-icon": { fontSize: 16, color: T.ink500 },
    }}
  />
);

const OrderDetail = ({ order, loanDocs, loanLoaded, nameOf, onAction }) => {
  const dispatch = useDispatch();
  const status = order?.status;
  const findDoc = (oid) =>
    oid
      ? (loanDocs || []).find((d) => oidStr(d?._id) === String(oid)) || null
      : null;
  const preview = (doc) => dispatch(filePreviewSetData(doc));

  const received = order?.received || null;
  const instructions =
    typeof order?.instructions === "string" ? order.instructions.trim() : "";
  const primaryOids = order?.primaryAppraisalDocOids || [];
  const history = [...(order?.history || [])].reverse();
  const today = todayYmd();

  const facts = [
    ["Vendor", vendorLabel(order)],
    order?.loanNumber != null ? ["Loan", `#${order.loanNumber}`] : null,
    order?.requestedBy ? ["Ordered by", nameOf(order.requestedBy)] : null,
    order?.createdAt
      ? ["Ordered", formatDateTime(order.createdAt, "MMM d, yyyy")]
      : null,
    order?.requestedByDate
      ? [
          "Needed by",
          `${formatDate(order.requestedByDate, "", "MMM d, yyyy")}${
            isOverdue(order, today) ? " · past due" : ""
          }`,
        ]
      : null,
    order?.providerOrderId ? ["Vendor order #", order.providerOrderId] : null,
    status === "cancelled" && order?.cancelReason
      ? ["Cancel reason", order.cancelReason]
      : null,
  ].filter(Boolean);

  const receivedFacts = received
    ? [
        received.asIsValue != null
          ? ["As-is value", formatDollar(received.asIsValue, "—")]
          : null,
        received.arv != null
          ? ["After-repair value", formatDollar(received.arv, "—")]
          : null,
        received.effectiveDate
          ? [
              "Effective date",
              formatDate(received.effectiveDate, "", "MMM d, yyyy"),
            ]
          : null,
      ].filter(Boolean)
    : [];
  const receivedDoc = findDoc(received?.documentOid);

  return (
    <Box>
      <Box sx={{ px: "18px", pt: "16px", pb: "14px" }}>
        <Box sx={{ fontSize: 15, fontWeight: 700, color: T.ink900, mb: 1 }}>
          {order?.subjectPropertyAddress || "Subject property"}
        </Box>
        <SecondaryStatusChip status={status} />
        {(canMarkSubmitted(order) ||
          canMarkReceived(order) ||
          canCancel(order)) && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.75 }}>
            {canMarkSubmitted(order) && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => onAction(SECONDARY_ACTION.SUBMITTED, order)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Mark placed
              </Button>
            )}
            {canMarkReceived(order) && (
              <Button
                variant="contained"
                size="small"
                onClick={() => onAction(SECONDARY_ACTION.RECEIVED, order)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Mark received
              </Button>
            )}
            {canCancel(order) && (
              <Button
                variant="text"
                color="error"
                size="small"
                onClick={() => onAction(SECONDARY_ACTION.CANCEL, order)}
                sx={{ textTransform: "none", fontWeight: 600, ml: "auto" }}
              >
                Cancel order
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Section label="Order">
        {facts.map(([k, v]) => (
          <Fact key={k} k={k} v={v} />
        ))}
      </Section>

      <Section label="Instructions">
        <Box
          sx={{
            fontSize: 12.5,
            lineHeight: 1.55,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            color: instructions ? T.ink700 : T.ink300,
          }}
        >
          {instructions || "No instructions were provided."}
        </Box>
      </Section>

      <Section label={`Appraisal to check against (${primaryOids.length})`}>
        {primaryOids.length === 0 ? (
          <Box sx={{ fontSize: 12.5, color: T.ink300 }}>—</Box>
        ) : !loanLoaded ? (
          <CircularProgress size={16} />
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {primaryOids.map((oid) => (
              <DocChip key={String(oid)} doc={findDoc(oid)} onPreview={preview} />
            ))}
          </Box>
        )}
      </Section>

      {received && (
        <Section label="Received">
          {receivedFacts.length === 0 && !received.documentOid ? (
            <Box sx={{ fontSize: 12.5, color: T.ink300 }}>
              No values were entered.
            </Box>
          ) : null}
          {receivedFacts.map(([k, v]) => (
            <Fact key={k} k={k} v={v} />
          ))}
          {received.documentOid && (
            <Box sx={{ mt: 1 }}>
              {loanLoaded ? (
                <DocChip doc={receivedDoc} onPreview={preview} />
              ) : (
                <CircularProgress size={16} />
              )}
            </Box>
          )}
        </Section>
      )}

      {history.length > 0 && (
        <Section label="History">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {history.map((e, i) => (
              <Box key={`${e?.at}-${i}`} sx={{ fontSize: 12.5 }}>
                <Box sx={{ color: T.ink900, fontWeight: 600 }}>
                  {secondaryStatusMeta(e?.status).label}
                  <Box
                    component="span"
                    sx={{ color: T.ink500, fontWeight: 400 }}
                  >
                    {e?.at ? ` · ${formatDateTime(e.at, "MMM d, yyyy h:mm a")}` : ""}
                    {e?.by ? ` · ${nameOf(e.by)}` : ""}
                  </Box>
                </Box>
                {e?.note && (
                  <Box sx={{ color: T.ink700, whiteSpace: "pre-wrap" }}>
                    {e.note}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Section>
      )}
    </Box>
  );
};

/* ── split view (`:id/*`) — list rail + order detail + embedded drilldown ── */

const RailRow = ({ order, selected, onClick }) => {
  const meta = secondaryStatusMeta(order?.status);
  const due = order?.requestedByDate
    ? formatDate(order.requestedByDate, "", "MMM d")
    : null;
  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: "pointer",
        p: "10px 13px",
        borderBottom: `1px solid ${T.line}`,
        borderLeft: `2px solid ${selected ? meta.tone.solid : "transparent"}`,
        background: selected ? "#f7f9fa" : "transparent",
        "&:hover": { background: "#f7f9fa" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: "3px",
        }}
      >
        <Box
          sx={{
            fontSize: 12.5,
            fontWeight: 600,
            color: T.ink900,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {order?.subjectPropertyAddress || "Subject property"}
        </Box>
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: meta.tone.solid,
            flexShrink: 0,
          }}
        />
      </Box>
      <Box
        sx={{
          fontSize: 11,
          color: T.ink500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {[
          meta.label,
          order?.loanNumber != null ? `#${order.loanNumber}` : null,
          order?.provider || KIND_LABEL[order?.kind] || null,
          due ? `due ${due}` : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      </Box>
    </Box>
  );
};

const SplitView = ({ rows, filtered, search, setSearch, loading, onReload, onPatch }) => {
  const navigate = useNavigate();
  const { id: dealId } = useParams();
  const [params] = useSearchParams();
  const orderParam = params.get("order");
  const loanDrilldown = useSelector((s) => s.loanDrilldown);
  const users = useSelector((s) => s.users);
  const nameOf = useCallback(
    (email) => (email ? getUserFullNameFromEmail(users, email) || email : null),
    [users],
  );
  // { type, order } for the open Mark placed / Mark received / Cancel modal.
  const [action, setAction] = useState(null);

  const selected = useMemo(
    () =>
      (orderParam && rows.find((o) => secondaryOrderId(o) === orderParam)) ||
      rows.find((o) => dealKey(o?.dealId) === dealId) ||
      null,
    [rows, orderParam, dealId],
  );

  // The embedded drilldown (column 3) loads the loan into Redux; documents
  // resolve from there once it is the selected order's loan.
  const loanLoaded = oidStr(loanDrilldown?._id) === dealId;
  const loanDocs = loanLoaded ? loanDrilldown?.loanDocuments || [] : [];

  return (
    <Box
      sx={{
        display: "flex",
        flex: "1 1 0",
        minHeight: 0,
        height: "100%",
        width: "100%",
        background: T.bg,
      }}
    >
      {/* Column 1 — list rail */}
      <Box
        sx={{
          width: 248,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          background: "#fff",
          borderRight: `1px solid ${T.line}`,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            height: 44,
            borderBottom: `1px solid ${T.line}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "6px",
            px: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
            <Tooltip title="Back to external orders" arrow>
              <Box
                component="button"
                type="button"
                onClick={() => navigate(OUTSIDE_BASE)}
                aria-label="Back to external orders"
                sx={{
                  font: "inherit",
                  color: T.teal600,
                  background: "transparent",
                  border: "none",
                  p: 0,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  "&:hover": { opacity: 0.75 },
                }}
              >
                <ArrowBackRounded sx={{ fontSize: 16 }} />
              </Box>
            </Tooltip>
            <Box sx={{ fontSize: 12.5, fontWeight: 600, color: T.ink900 }}>
              External orders
            </Box>
          </Box>
          <Box component="span" sx={{ fontSize: 12, color: T.ink500 }}>
            {filtered.length}
          </Box>
        </Box>

        <Box
          sx={{
            p: "10px 12px",
            borderBottom: `1px solid ${T.line}`,
            flexShrink: 0,
          }}
        >
          <SearchBox
            value={search}
            onChange={setSearch}
            placeholder="Search loan #, property, vendor…"
          />
        </Box>

        <Box sx={{ flex: "1 1 0", minHeight: 0, overflowY: "auto" }}>
          {loading ? (
            <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
              <CircularProgress size={22} />
            </Box>
          ) : filtered.length === 0 ? (
            <EmptyState hasAny={rows.length > 0} />
          ) : (
            filtered.map((o) => (
              <RailRow
                key={secondaryOrderId(o)}
                order={o}
                selected={secondaryOrderId(o) === secondaryOrderId(selected)}
                onClick={() => navigate(orderHref(o))}
              />
            ))
          )}
        </Box>
      </Box>

      {/* Column 2 — order detail */}
      <Box
        sx={{
          width: 380,
          flexShrink: 0,
          minHeight: 0,
          overflowY: "auto",
          background: "#fff",
          borderRight: `1px solid ${T.line}`,
        }}
      >
        {selected ? (
          <OrderDetail
            order={selected}
            loanDocs={loanDocs}
            loanLoaded={loanLoaded}
            nameOf={nameOf}
            onAction={(type, order) => setAction({ type, order })}
          />
        ) : (
          <Box sx={{ p: 4, textAlign: "center", color: T.ink300, fontSize: 13 }}>
            {loading ? "Loading…" : "Select an order to see its details."}
          </Box>
        )}
      </Box>

      {/* Column 3 — embedded loan drilldown */}
      <Box
        sx={{
          position: "relative",
          flex: "1 1 0",
          minWidth: 0,
          height: "100%",
          overflow: "hidden",
          background: "#f1f5f9",
        }}
      >
        <LoanDrilldownScreen embedded />
      </Box>

      <SecondaryOrderActionDialogs
        action={action}
        dealId={dealKey(action?.order?.dealId)}
        loanDocuments={loanDocs}
        onClose={() => setAction(null)}
        onUpdated={onPatch}
        onConflict={onReload}
      />
    </Box>
  );
};

/* ── the tab ────────────────────────────────────────────────────────────── */

const OutsideOrdersQueue = ({ loanIndex, loanIndexLoading }) => {
  const user = useSelector((state) => state.user);
  const myEmail = (user?.emailAddress || user?.email || "").toLowerCase();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lane, setLane] = useState(ALL);
  const [scope, setScope] = useState(SCOPE.ALL);
  const [search, setSearch] = useState("");
  const [mineOnly, setMineOnly] = useState(false);
  const [hideInactive, setHideInactive] = useState(() =>
    readHideInactive(HIDE_INACTIVE_KEY),
  );

  useEffect(() => {
    writeHideInactive(HIDE_INACTIVE_KEY, hideInactive);
  }, [hideInactive]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    secondaryValuationOrdersApi
      .queue()
      .then(setOrders)
      .catch((err) =>
        setError(
          secondaryOrderErrorMessage(err, "The external orders couldn't be loaded."),
        ),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patchOrder = useCallback((updated) => {
    const id = secondaryOrderId(updated);
    if (!id) return;
    setOrders((prev) =>
      prev.map((o) => (secondaryOrderId(o) === id ? { ...o, ...updated } : o)),
    );
  }, []);

  // Every order stamped with its loan's stage + closing date; the loan number
  // falls back to the pipeline's when the order didn't store one.
  const rows = useMemo(
    () =>
      orders.map((o) => {
        const r = withLoanStage(
          o,
          loanIndex,
          o?.dealCollection === "loan_acquisitions",
        );
        return { ...r, loanNumber: o?.loanNumber ?? r._loan?.loanNumber ?? null };
      }),
    [orders, loanIndex],
  );

  const activeRows = useMemo(
    () => (hideInactive ? rows.filter((o) => !o.loanInactive) : rows),
    [rows, hideInactive],
  );
  const hiddenCount = rows.length - activeRows.length;

  const scopeCounts = useMemo(
    () => ({
      [SCOPE.ALL]: activeRows.length,
      [SCOPE.PRECLOSE]: activeRows.filter((o) => inScope(o, SCOPE.PRECLOSE))
        .length,
      [SCOPE.ACQ]: activeRows.filter((o) => inScope(o, SCOPE.ACQ)).length,
    }),
    [activeRows],
  );

  const visibleRows = useMemo(
    () => activeRows.filter((o) => inScope(o, scope)),
    [activeRows, scope],
  );

  const laneCounts = useMemo(() => {
    const counts = { [ALL]: visibleRows.length };
    LANES.forEach(({ key }) => {
      if (key !== ALL)
        counts[key] = visibleRows.filter((o) => o.status === key).length;
    });
    return counts;
  }, [visibleRows]);

  const stats = useMemo(() => {
    const today = todayYmd();
    const by = (s) => visibleRows.filter((o) => o.status === s).length;
    return {
      requested: by("requested"),
      submitted: by("submitted"),
      received: by("received"),
      overdue: visibleRows.filter((o) => isOverdue(o, today)).length,
    };
  }, [visibleRows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return visibleRows.filter(
      (o) =>
        (lane === ALL || o.status === lane) &&
        (!mineOnly || (o.requestedBy || "").toLowerCase() === myEmail) &&
        matchesSearch(o, q),
    );
  }, [visibleRows, lane, mineOnly, myEmail, search]);

  const users = useSelector((s) => s.users);
  const nameOf = useCallback(
    (email) => (email ? getUserFullNameFromEmail(users, email) || email : null),
    [users],
  );

  const columnDefs = useMemo(() => {
    const today = todayYmd();
    return [
      {
        headerName: "Status",
        field: "status",
        width: 170,
        filter: "agSetColumnFilter",
        filterParams: {
          valueFormatter: (p) => secondaryStatusMeta(p.value).label,
        },
        cellRenderer: (p) => (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <SecondaryStatusChip status={p.value} />
          </Box>
        ),
      },
      {
        headerName: "Loan",
        field: "loanNumber",
        width: 130,
        filter: "agTextColumnFilter",
        filterValueGetter: (p) =>
          p.data?.loanNumber != null ? String(p.data.loanNumber) : "",
        cellRenderer: (p) => {
          const id = dealKey(p.data?.dealId);
          const label = p.value != null ? `#${p.value}` : "Open loan";
          if (!id) return <Box component="span">—</Box>;
          return (
            <Box
              component="a"
              href={`${p.data?.loanIsAcquisition ? "/acquisition" : "/loan"}/${id}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "5px",
                color: T.teal600,
                fontWeight: 600,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {label}
              <OpenInNewRounded sx={{ fontSize: 13 }} />
            </Box>
          );
        },
      },
      correspondentColumnDef({ loaded: Boolean(loanIndex) }),
      ...loanStageColumnDefs({ loaded: Boolean(loanIndex) }),
      {
        headerName: "Subject property",
        field: "subjectPropertyAddress",
        flex: 1.4,
        minWidth: 200,
        filter: "agTextColumnFilter",
        cellRenderer: (p) => (
          <Box
            title={p.value || ""}
            sx={{
              color: T.ink900,
              fontWeight: 500,
              minWidth: 0,
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {p.value || "—"}
          </Box>
        ),
      },
      {
        headerName: "Vendor",
        colId: "vendor",
        flex: 1,
        minWidth: 160,
        valueGetter: (p) => vendorLabel(p.data),
        filter: "agSetColumnFilter",
      },
      {
        headerName: "Needed by",
        colId: "requestedByDate",
        flex: 1,
        minWidth: 170,
        valueGetter: (p) => p.data?.requestedByDate || "",
        filter: "agDateColumnFilter",
        filterParams: { comparator: dateOnlyComparator },
        cellRenderer: (p) => {
          const due = p.data?.requestedByDate
            ? formatDate(p.data.requestedByDate, "")
            : "—";
          const late = isOverdue(p.data, today);
          const by = p.data?.requestedBy ? nameOf(p.data.requestedBy) : null;
          return (
            <Box sx={{ lineHeight: 1.25 }}>
              <Box
                sx={{
                  color: late ? statusTones.error.fg : T.ink900,
                  fontSize: 13,
                  fontWeight: late ? 600 : 400,
                }}
              >
                {due}
                {late ? " · past due" : ""}
              </Box>
              {by && (
                <Box sx={{ fontSize: 11.5, color: T.ink500 }}>by {by}</Box>
              )}
            </Box>
          );
        },
      },
      {
        headerName: "Vendor order #",
        field: "providerOrderId",
        width: 150,
        filter: "agTextColumnFilter",
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "Received",
        colId: "received",
        width: 160,
        valueGetter: (p) => p.data?.received?.asIsValue ?? null,
        filter: false,
        cellRenderer: (p) => <ReceivedCell order={p.data} />,
      },
      {
        headerName: "Ordered",
        field: "createdAt",
        flex: 0.8,
        minWidth: 130,
        filter: "agDateColumnFilter",
        filterParams: { comparator: dateOnlyComparator },
        valueFormatter: (p) => (p.value ? formatDateTime(p.value, "date") : "—"),
      },
    ];
  }, [loanIndex, nameOf]);

  const busy = loading || loanIndexLoading;

  return (
    <Routes>
      <Route
        index
        element={
          <FullView
            rows={rows}
            filtered={filtered}
            stats={stats}
            laneCounts={laneCounts}
            lane={lane}
            setLane={setLane}
            scope={scope}
            setScope={setScope}
            scopeCounts={scopeCounts}
            search={search}
            setSearch={setSearch}
            mineOnly={mineOnly}
            setMineOnly={setMineOnly}
            hideInactive={hideInactive}
            setHideInactive={setHideInactive}
            hiddenCount={hiddenCount}
            loading={busy}
            error={error}
            columnDefs={columnDefs}
            onRefresh={load}
          />
        }
      />
      <Route
        path=":id/*"
        element={
          <SplitView
            rows={rows}
            filtered={filtered}
            search={search}
            setSearch={setSearch}
            loading={busy}
            onReload={load}
            onPatch={patchOrder}
          />
        }
      />
    </Routes>
  );
};

export default OutsideOrdersQueue;
